import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { t } from '../i18n';
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format';
import type { Doc } from '../lib/types';
import { Button, Ltr, PaymentBadge, QuoteBadge, SkeletonRows, SortTh } from './ui';
import { MarkPaidDialog } from './MarkPaid';
import { IconStar } from './icons';
import { useOpenDocument } from './Layout';
import { useCan } from '../lib/auth';
import { ProfitBadge, useDocProfits } from './Margin';

export type DocColumn =
  | 'type'
  | 'number'
  | 'date'
  | 'customer'
  | 'description'
  | 'subtotal'
  /** gross margin — shown only to users with the profit permission */
  | 'margin'
  | 'vat'
  | 'total'
  | 'payment'
  | 'due'
  | 'open'
  | 'quoteStatus'
  | 'address'
  | 'billed'
  | 'source'
  | 'imported';

type SortKey = 'issueDate' | 'number' | 'total' | 'dueDate' | 'importedAt';
const SORTABLE: Partial<Record<DocColumn, SortKey>> = { number: 'number', date: 'issueDate', total: 'total', due: 'dueDate', imported: 'importedAt' };
const NUMERIC: DocColumn[] = ['subtotal', 'margin', 'vat', 'total', 'open'];
const PROFIT_TYPES = ['tax_invoice', 'tax_invoice_receipt', 'delivery_note'];
const HIDE_SM: DocColumn[] = ['subtotal', 'vat', 'source', 'imported', 'description', 'address', 'due'];

const HEAD: Record<DocColumn, string> = {
  type: t.docs.cols.type,
  number: t.docs.cols.number,
  date: t.docs.cols.date,
  customer: t.docs.cols.customer,
  description: t.docs.cols.description,
  subtotal: t.docs.cols.subtotal,
  margin: 'רווחיות',
  vat: t.docs.cols.vat,
  total: t.docs.cols.total,
  payment: t.docs.cols.payment,
  due: t.docs.cols.due,
  open: t.docs.cols.open,
  quoteStatus: t.docs.cols.status,
  address: t.docs.cols.address,
  billed: t.docs.cols.billed,
  source: t.docs.cols.source,
  imported: t.docs.cols.imported,
};

export function DocTable({
  docs,
  columns: requested,
  loading,
  sort,
  dir,
  onSort,
  empty,
  selectable,
}: {
  /** Checkbox column for marking several open invoices as paid at once. */
  selectable?: boolean;
  docs: Doc[] | undefined;
  columns: DocColumn[];
  loading?: boolean;
  sort?: SortKey;
  dir?: 'asc' | 'desc';
  onSort?: (k: SortKey) => void;
  empty?: ReactNode;
}) {
  const open = useOpenDocument();
  const today = new Date().toISOString().slice(0, 10);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [markTargets, setMarkTargets] = useState<Doc[] | null>(null);
  const can = useCan();
  const columns = can('profit.view') ? requested : requested.filter((c) => c !== 'margin');
  const profitIds = columns.includes('margin') ? (docs ?? []).filter((d) => PROFIT_TYPES.includes(d.type) && d.paymentStatus !== 'cancelled').map((d) => d.id) : [];
  const profits = useDocProfits(profitIds);
  const canMark = (d: Doc) => can('payments.edit') && d.type === 'tax_invoice' && (d.open ?? 0) > 0.009;
  const markable = (docs ?? []).filter(canMark);
  // Drop selections that are no longer on the page or no longer open.
  useEffect(() => {
    setSelected((s) => new Set([...s].filter((id) => markable.some((d) => d.id === id))));
  }, [docs]);

  const cell = (d: Doc, c: DocColumn): ReactNode => {
    switch (c) {
      case 'type':
        return d.typeLabel;
      case 'number':
        return (
          <span className="row" style={{ gap: 6 }}>
            <Ltr className="tabular strong">{d.number ?? '—'}</Ltr>
            {d.isPrimaryQuote ? <IconStar size={13} filled style={{ color: 'var(--accent)' }} aria-label={t.docs.primary} /> : null}
          </span>
        );
      case 'date':
        return <span className="tabular">{fmtDate(d.issueDate)}</span>;
      case 'customer':
        return d.customerId ? (
          <Link to={`/customers/${d.customerId}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--ink)' }}>
            {d.customerName}
          </Link>
        ) : (
          <span className="faint">—</span>
        );
      case 'description':
        return <span className="ellipsis" style={{ display: 'block', maxWidth: 260 }} title={d.description ?? ''}>{d.description ?? <span className="faint">—</span>}</span>;
      case 'subtotal':
        return fmtMoney(d.subtotal);
      case 'margin':
        return PROFIT_TYPES.includes(d.type) && d.paymentStatus !== 'cancelled' ? (
          profits.data ? <ProfitBadge p={profits.data.items[d.id]} /> : <span className="faint">…</span>
        ) : (
          <span className="faint">—</span>
        );
      case 'vat':
        return fmtMoney(d.vat);
      case 'total':
        // Credits reduce the balance: shown with a minus, isolated LTR so the sign stays in front of the number.
        return d.type === 'credit_note' && d.total ? (
          <span className="muted">{fmtMoney(-d.total)}</span>
        ) : d.paymentStatus === 'cancelled' ? (
          <span className="amount" title="החשבונית בוטלה בחשבונית זיכוי ואינה נספרת במכירות וביתרות">{fmtMoney(d.total)}</span>
        ) : (
          <span className="strong">{fmtMoney(d.total)}</span>
        );
      case 'payment':
        return (
          <span className="row" style={{ gap: 6 }}>
            <PaymentBadge status={d.paymentStatus} />
            {canMark(d) ? (
              <Button
                size="sm"
                variant="ghost"
                className="mark-paid-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setMarkTargets([d]);
                }}
                title="סימון החשבונית כשולמה (ללא קבלה)"
              >
                סמן כשולם
              </Button>
            ) : null}
          </span>
        );
      case 'due':
        return d.dueDate && d.paymentStatus !== 'cancelled' ? (
          <span className="tabular" style={d.paymentStatus === 'overdue' || (d.dueDate < today && (d.open ?? 0) > 0) ? { color: 'var(--bad)' } : undefined}>{fmtDate(d.dueDate)}</span>
        ) : (
          <span className="faint">—</span>
        );
      case 'open':
        return d.open ? fmtMoney(d.open) : <span className="faint">—</span>;
      case 'quoteStatus':
        return <QuoteBadge status={d.quoteStatus} />;
      case 'address':
        return <span className="ellipsis" style={{ display: 'block', maxWidth: 220 }}>{d.deliveryAddress ?? <span className="faint">—</span>}</span>;
      case 'billed':
        return d.billedInvoiceId ? <span className="badge ok">חויבה</span> : <span className="badge warn">{t.docs.notBilled}</span>;
      case 'source':
        return <span className="muted small">{t.enums.source[d.source]}</span>;
      case 'imported':
        return <span className="muted small tabular" title={fmtDateTime(d.importedAt)}>{fmtDate(d.importedAt)}</span>;
    }
  };

  const selectedDocs = markable.filter((d) => selected.has(d.id));
  return (
    <div className="table-wrap">
      {selectable && selectedDocs.length ? (
        <div className="bulk-bar">
          <span>
            נבחרו <strong>{selectedDocs.length}</strong> חשבוניות · <span className="tabular">{fmtMoney(selectedDocs.reduce((s, d) => s + (d.open ?? 0), 0))}</span> פתוח
          </span>
          <span className="spacer" />
          <Button size="sm" variant="primary" onClick={() => setMarkTargets(selectedDocs)}>סימון כשולמו</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>ניקוי בחירה</Button>
        </div>
      ) : null}
      {markTargets ? (
        <MarkPaidDialog
          invoices={markTargets.map((d) => ({ id: d.id, number: d.number, open: d.open, customerName: d.customerName }))}
          onClose={() => setMarkTargets(null)}
          onDone={() => setSelected(new Set())}
        />
      ) : null}
      <table className="table">
        <thead>
          <tr>
            {selectable ? (
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  aria-label="בחירת כל החשבוניות הפתוחות בעמוד"
                  checked={markable.length > 0 && selected.size === markable.length}
                  disabled={markable.length === 0}
                  onChange={(e) => setSelected(e.target.checked ? new Set(markable.map((d) => d.id)) : new Set())}
                />
              </th>
            ) : null}
            {columns.map((c) => {
              const cls = `${NUMERIC.includes(c) ? 'num' : ''} ${HIDE_SM.includes(c) ? 'hide-sm' : ''}`;
              const sk = SORTABLE[c];
              return sk && onSort && sort && dir ? (
                <SortTh key={c} label={HEAD[c]} sortKey={sk} sort={sort} dir={dir} onSort={onSort} className={cls} />
              ) : (
                <th key={c} className={cls}>{HEAD[c]}</th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows cols={columns.length + (selectable ? 1 : 0)} rows={6} />
          ) : docs && docs.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} style={{ height: 'auto' }}>{empty}</td>
            </tr>
          ) : (
            docs?.map((d) => (
              <tr key={d.id} className={`clickable ${selected.has(d.id) ? 'selected' : ''} ${d.paymentStatus === 'cancelled' ? 'cancelled' : ''}`} onClick={() => open(d.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && open(d.id)}>
                {selectable ? (
                  <td onClick={(e) => e.stopPropagation()}>
                    {canMark(d) ? (
                      <input
                        type="checkbox"
                        aria-label={`בחירת חשבונית ${d.number ?? ''}`}
                        checked={selected.has(d.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(d.id);
                          else next.delete(d.id);
                          setSelected(next);
                        }}
                      />
                    ) : null}
                  </td>
                ) : null}
                {columns.map((c) => (
                  <td key={c} className={`${NUMERIC.includes(c) ? 'num' : ''} ${HIDE_SM.includes(c) ? 'hide-sm' : ''}`}>
                    {cell(d, c)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
